import { isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
	Box,
	Button,
	Divider,
	InputLabel,
	Menu,
	MenuDropdown,
	MenuOption,
	MenuOptionList,
	PopoverTrigger,
	Select,
	SelectOption,
	Typography
} from "@launchmetrics/lm-ui";

import { useFeedFinder } from "../hooks";
import { FeedObjectFocusName } from "class/Feed";
import { PLACEMENTS_COUNT } from "./consts";
import { removeReportClipping, setReportClipping, clearReportClippings } from "store/article/report/reducers";
import { ReactComponent as CloseFilledIcon } from 'vendor/svg/report/close-filled.svg';

import { useStyles } from "components/Article/Report/styles";

import type { FeedClippingCount, ReportFeedClipping } from "store/models/reportConfiguration";
import type { State } from "store/types";

export const FeedClipping: React.FC = () => {
	const { reportForm } = useStyles();
	const { t } = useTranslation();
	const dispatch = useDispatch();

	const disabled = useSelector((state: State) => state.article.report.formDisabled);
	const filterFeedIds = useSelector((state: State) => state.search.filters.feeds);
	const feedClippings = useSelector((state: State) => state.article.report.configuration.definition.settings.feedClippings);
	const reportId = useSelector((state: State) => state.article.report.configuration.id);
	const filterFeeds = useFeedFinder(filterFeedIds);

	const [selectedFeedClippingIds, setSelectedFeedClippingIds] = useState<string[]>([]);
	const [openFeedClippingDropdown, setOpenFeedClippingDropdown] = useState(false);
	const [feedClippingSearch, setFeedClippingSearch] = useState('');
	const selectedFeeds = useFeedFinder(selectedFeedClippingIds);

	const firstRender = useRef(true);

	useEffect(() => {
		// There is a render where filterIds is not set yet and it's removing the feedClippings
		if (firstRender.current && reportId) {
			firstRender.current = false;
			return;
		}
		const feedClippingsIds: string[] = [];
		for (const clipping of feedClippings) {
			if (!filterFeedIds.includes(clipping.id)) dispatch(removeReportClipping({feedId: clipping.id}));
			else feedClippingsIds.push(clipping.id);
		}
		setSelectedFeedClippingIds(feedClippingsIds);
		firstRender.current = false;
	}, [filterFeedIds]); // eslint-disable-line react-hooks/exhaustive-deps

	const onFeedClippingSelectionClear = useCallback(() => {
		setSelectedFeedClippingIds([]);
		setFeedClippingSearch('');
		dispatch(clearReportClippings());
	}, [setSelectedFeedClippingIds, setFeedClippingSearch, dispatch]);

	const onToggleFeedClippingSelection = useCallback((feedId: string) => {
		const selectedIndex = selectedFeedClippingIds.indexOf(feedId);
		let newSelectedFeedIds;
		if (selectedIndex > -1) {
			newSelectedFeedIds = selectedFeedClippingIds.filter(id => id !== feedId);
			dispatch(removeReportClipping({feedId}));
		}
		else {
			newSelectedFeedIds = [...selectedFeedClippingIds, feedId];
			dispatch(setReportClipping({feedId, count: 10}));
		}
		setSelectedFeedClippingIds(newSelectedFeedIds);
	}, [selectedFeedClippingIds, dispatch, setSelectedFeedClippingIds]);

	const feedsByFocus = useMemo(() => {
		return filterFeeds
			.filter(feed => isEmpty(feedClippingSearch) || feed.name.toLowerCase().includes(feedClippingSearch.toLowerCase()))
			.reduce((acc: {[focusName: string]: FeedObjectFocusName[]}, feed) => {
				if (!acc[feed.focusName]) acc[feed.focusName] = [];
				acc[feed.focusName].push(feed);
				return acc;
			}, {});
	}, [filterFeeds, feedClippingSearch]);

	const feedClippingsSelector = useMemo(() => {
		return (
			<Menu
				onChange={() => {}}
				searchable={true}
				searchInputValue={feedClippingSearch}
				onSearchInputChange={search => setFeedClippingSearch(search)}
				onOpenChange={() => setOpenFeedClippingDropdown(!openFeedClippingDropdown)}
				popoverProps={{placement: 'top-end'}}
				open={openFeedClippingDropdown}
				selectedValues={selectedFeedClippingIds}
			>
				<PopoverTrigger>
					<Button id="feed-clipping-btn" onClick={() => setOpenFeedClippingDropdown(!openFeedClippingDropdown)} variant="textGray" size="small" disabled={disabled}>
						+ {t('add').toUpperCase()}
					</Button>
				</PopoverTrigger>
				<MenuDropdown
					onClear={() => onFeedClippingSelectionClear()}
					styleProps={reportForm.powerpoint.feedClipping.dropdown}
				>
					<MenuOptionList>
						{Object.keys(feedsByFocus).map((focusName, focusIndex) => (
							<Fragment key={focusIndex}>
								<Box styleProps={reportForm.selectorTitle}>{focusName}</Box>
								{feedsByFocus[focusName].map((feed, feedIndex) => (
									<MenuOption
										id={`feed-clipping-selector-option-${feed.id}`}
										key={feedIndex}
										value={feed.id}
										onClick={() => onToggleFeedClippingSelection(feed.id)}
									>
										{feed.name}
									</MenuOption>
								))}
								{focusIndex < Object.keys(feedsByFocus).length - 1 &&  <Divider />}
							</Fragment>
						))}
					</MenuOptionList>
				</MenuDropdown>
			</Menu>
		);
	}, [
		openFeedClippingDropdown, feedClippingSearch, selectedFeedClippingIds, reportForm, feedsByFocus, disabled,
		onFeedClippingSelectionClear, onToggleFeedClippingSelection, setFeedClippingSearch, setOpenFeedClippingDropdown, t
	]);

	const feedClippingsConfiguration = useMemo(() => {
		return (<>
			{selectedFeeds && selectedFeeds.map((feed, index) => {
				const feedClipping = feedClippings.find((clipping: ReportFeedClipping) => clipping.id === feed.id) || { id: '', count: '10' };
				return (
					<Box className="side-panel-inline placement-row" key={index}>
						<InputLabel className="feed-name">{feed.name}</InputLabel>
						<Select
							id={`feed-clipping-${feed.id}`}
							placeholder={t('reports.content_text') as string}
							optionValue={{
								key: feedClipping.count.toString(),
								value: feedClipping.count.toString()
							}}
							disabled={disabled}
							styleProps={reportForm.powerpoint.feedClipping.count}
						>
							{PLACEMENTS_COUNT.map((count, index) => (
								<SelectOption
									id={`feed-clipping-count-${feed.id}-${count}`}
									selected={feedClipping.count === count}
									key={index}
									onClick={() =>
										dispatch(setReportClipping({feedId: feed.id, count: count as FeedClippingCount}))
									}
									optionValue={{
										key: count.toString(),
										value: count.toString()
									}}
								/>
							))}
						</Select>
						<Box>
							<Button
								id={`removeClippingBtn-${feed.id}`}
								onClick={() => onToggleFeedClippingSelection(feed.id)}
								leftIcon={<CloseFilledIcon />}
								variant='textGray' size='small'
								styleProps={reportForm.powerpoint.feedClipping.remove}
							/>
						</Box>
					</Box>
				);
			})}
		</>);
	}, [selectedFeeds, disabled, feedClippings, reportForm, onToggleFeedClippingSelection, dispatch, t]);

	return (
		<>
			<Box className='side-panel-inline'>
				<Typography styleProps={reportForm.feedClippingLabel} variant='textSmMedium'>{t('reports.side_panel.feed_clippings.label')}</Typography>
				{feedClippingsSelector}
			</Box>
			<Box className='side-panel-content-entry-element'>
				{feedClippingsConfiguration}
			</Box>
		</>
	);
};
